import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  LayoutGrid, Library, Megaphone, Layout, Video, Mail,
  FolderHeart, Plus, LogOut, ChevronDown, ChevronRight,
  Settings, Sparkles
} from "lucide-react";

const sectionItems = [
  { id: "library", label: "All Content", icon: Library, gradient: "from-violet-500 to-purple-500" },
  { id: "ads", label: "Ads", icon: Megaphone, gradient: "from-blue-500 to-cyan-500" },
  { id: "landing_pages", label: "Landing Pages", icon: Layout, gradient: "from-emerald-500 to-teal-500" },
  { id: "ugc", label: "UGC & Reels", icon: Video, gradient: "from-pink-500 to-rose-500" },
  { id: "emails", label: "Emails", icon: Mail, gradient: "from-amber-500 to-orange-500" },
];

const Sidebar = ({ activeSection, onSectionChange, refreshKey, categories }) => {
  const { user, logout, api } = useAuth();
  const [boards, setBoards] = useState([]);
  const [boardsOpen, setBoardsOpen] = useState(true);
  const [categoriesOpen, setCategoriesOpen] = useState(true);
  const [newBoardName, setNewBoardName] = useState("");
  const [showNewBoard, setShowNewBoard] = useState(false);

  useEffect(() => {
    api.get("/boards").then((res) => setBoards(res.data)).catch(() => {});
  }, [api, refreshKey]);

  const createBoard = async () => {
    if (!newBoardName.trim()) return;
    try {
      const res = await api.post("/boards", { name: newBoardName.trim() });
      setBoards((prev) => [res.data, ...prev]);
      setNewBoardName("");
      setShowNewBoard(false);
    } catch {}
  };

  return (
    <aside className="w-[260px] shrink-0 bg-[#0c0c0e]/80 backdrop-blur-xl border-r border-white/[0.06] flex flex-col h-screen sticky top-0" data-testid="sidebar">
      {/* Logo */}
      <div className="px-5 h-16 flex items-center gap-3 border-b border-white/[0.06] shrink-0">
        <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-lg shadow-violet-500/20">
          <Sparkles size={16} className="text-white" />
        </div>
        <span className="text-white font-bold text-[15px] tracking-tight">AllSave</span>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-1">
        <p className="text-[10px] text-white/30 uppercase tracking-[0.2em] font-medium px-3 mb-3">Library</p>
        
        {sectionItems.map((item) => {
          const Icon = item.icon;
          const isActive = activeSection === item.id;
          return (
            <button
              key={item.id}
              onClick={() => onSectionChange(item.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 relative overflow-hidden ${
                isActive
                  ? "bg-white/[0.08] text-white"
                  : "text-white/50 hover:text-white/80 hover:bg-white/[0.04]"
              }`}
              data-testid={`nav-${item.id}`}
            >
              {/* Active indicator */}
              {isActive && (
                <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-6 rounded-r-full bg-gradient-to-b ${item.gradient}`} />
              )}
              
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 ${
                isActive 
                  ? `bg-gradient-to-br ${item.gradient} shadow-lg` 
                  : 'bg-white/[0.05]'
              }`}>
                <Icon size={15} className={isActive ? "text-white" : "text-white/50"} />
              </div>
              <span>{item.label}</span>
            </button>
          );
        })}

        {/* Categories Accordion */}
        <div className="pt-6">
          <button
            onClick={() => setCategoriesOpen(!categoriesOpen)}
            className="w-full flex items-center justify-between px-3 py-2 group"
          >
            <p className="text-[10px] text-white/30 uppercase tracking-[0.2em] font-medium">Categories</p>
            <ChevronDown 
              size={14} 
              className={`text-white/30 transition-transform duration-300 ease-out ${categoriesOpen ? 'rotate-0' : '-rotate-90'}`} 
            />
          </button>

          <div className={`space-y-0.5 overflow-hidden transition-all duration-300 ease-out ${categoriesOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
            {categories.length === 0 ? (
              <p className="text-[11px] text-white/20 px-3 py-2 italic">Aucune catégorie</p>
            ) : (
              categories.map((cat) => (
                <div 
                  key={cat.id} 
                  className="flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] text-white/50 hover:text-white/70 hover:bg-white/[0.03] transition-all cursor-pointer"
                >
                  <div 
                    className="w-3 h-3 rounded-full shrink-0 ring-2 ring-offset-1 ring-offset-[#0c0c0e]" 
                    style={{ backgroundColor: cat.color, ringColor: cat.color + '40' }} 
                  />
                  <span className="truncate flex-1">{cat.name}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Boards Accordion */}
        <div className="pt-4">
          <button
            onClick={() => setBoardsOpen(!boardsOpen)}
            className="w-full flex items-center justify-between px-3 py-2 group"
          >
            <p className="text-[10px] text-white/30 uppercase tracking-[0.2em] font-medium">Inspiration Boards</p>
            <div className="flex items-center gap-2">
              <button
                onClick={(e) => { e.stopPropagation(); setShowNewBoard(true); }}
                className="opacity-0 group-hover:opacity-100 w-5 h-5 rounded-md bg-white/[0.06] flex items-center justify-center text-white/40 hover:text-white hover:bg-white/[0.1] transition-all"
                data-testid="add-board-btn"
              >
                <Plus size={11} />
              </button>
              <ChevronDown 
                size={14} 
                className={`text-white/30 transition-transform duration-300 ease-out ${boardsOpen ? 'rotate-0' : '-rotate-90'}`} 
              />
            </div>
          </button>

          <div className={`space-y-0.5 overflow-hidden transition-all duration-300 ease-out ${boardsOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
            {showNewBoard && (
              <div className="px-2 py-2">
                <input
                  value={newBoardName}
                  onChange={(e) => setNewBoardName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && createBoard()}
                  onBlur={() => { if (!newBoardName.trim()) setShowNewBoard(false); }}
                  placeholder="Nom du board..."
                  className="w-full bg-white/[0.05] border border-white/[0.1] rounded-lg px-3 py-2 text-[12px] text-white placeholder:text-white/20 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20 transition-all"
                  autoFocus
                />
              </div>
            )}
            {boards.length === 0 && !showNewBoard ? (
              <p className="text-[11px] text-white/20 px-3 py-2 italic">Aucun board</p>
            ) : (
              boards.map((board) => {
                const isActive = activeSection === "board";
                return (
                  <button
                    key={board.id}
                    onClick={() => onSectionChange("board", board.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] transition-all duration-200 ${
                      isActive
                        ? "bg-white/[0.06] text-white"
                        : "text-white/50 hover:text-white/70 hover:bg-white/[0.03]"
                    }`}
                    data-testid={`board-${board.id}`}
                  >
                    <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-pink-500/20 to-violet-500/20 flex items-center justify-center">
                      <FolderHeart size={13} className="text-pink-400" />
                    </div>
                    <span className="truncate flex-1 text-left">{board.name}</span>
                    <span className="text-[10px] text-white/20 bg-white/[0.05] rounded-full px-2 py-0.5">{board.content_ids?.length || 0}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </nav>

      {/* User footer */}
      <div className="px-3 py-4 border-t border-white/[0.06] shrink-0 space-y-2">
        {/* Settings button */}
        <button
          onClick={() => onSectionChange("settings")}
          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[13px] font-medium transition-all duration-200 ${
            activeSection === "settings"
              ? "bg-white/[0.08] text-white"
              : "text-white/50 hover:text-white/70 hover:bg-white/[0.04]"
          }`}
          data-testid="nav-settings"
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            activeSection === "settings" ? 'bg-white/[0.1]' : 'bg-white/[0.05]'
          }`}>
            <Settings size={15} className={activeSection === "settings" ? "text-white" : "text-white/50"} />
          </div>
          Paramètres
        </button>

        {/* User info */}
        <div className="flex items-center justify-between px-3 py-2 rounded-xl bg-white/[0.03]">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-[11px] text-white font-bold shrink-0">
              {user?.username?.[0]?.toUpperCase()}
            </div>
            <span className="text-[13px] text-white/70 truncate">{user?.username}</span>
          </div>
          <button 
            onClick={logout} 
            className="w-8 h-8 rounded-lg bg-white/[0.05] flex items-center justify-center text-white/30 hover:text-white/70 hover:bg-white/[0.1] transition-all" 
            title="Logout" 
            data-testid="logout-btn"
          >
            <LogOut size={14} />
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
